import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { KeycloakService } from './services/keycloak.service';

/**
 * Handles HTTP error responses.
 *
 * On 401: attempts a token refresh via KC. If refresh succeeds, retries
 * the original request once with the new token. If the refresh itself
 * fails the KC session is dead, so login() shows an actual login form.
 * If the retry still 401s with a FRESH token, the BE is rejecting this
 * identity (e.g. a tenant host the user has no access to) — a plain
 * login() would silently SSO the same user back in and reload-loop
 * forever, so route to the switch-account page instead, where ending
 * the SSO session to sign in as someone else is an explicit choice.
 *
 * The two rules that keep that promise, both learned from a live reload
 * loop on /company-management/materials (three filter dropdowns 401 in the
 * same tick, the page reloads through Keycloak forever):
 *
 *  1. While a KC session exists, a 401 NEVER triggers login(). Keycloak
 *     answers the redirect from its live session without prompting, the app
 *     boots, the same requests 401 again — a full-page loop that also wipes
 *     the console before anyone can read the failure.
 *  2. Concurrent 401s share ONE refresh. The old isRefreshing flag only
 *     covered the first of them; every other in-flight 401 fell through to
 *     the blanket login() below and started that loop.
 *
 * On 403: redirects to /error/unauthorized so the user sees a clean page
 * instead of a silent blank screen.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  public redirectToLogin = true;

  /** Shared by every 401 in the same burst so they refresh once, not N times. */
  private refreshInFlight: Promise<string> | null = null;

  /** One-shot: a fresh token was already rejected, so stop reacting to 401s. */
  private identityRejected = false;

  constructor(
    private readonly router: Router,
    private readonly keycloak: KeycloakService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 403) {
          console.warn('403 Forbidden:', err.url);
          // Only redirect for company/page-level access errors, not inline API rejections
          // (e.g. role-gated PATCH returns 403 → frontend shows error toast, doesn't redirect)
        }

        if (err.status !== 401) {
          return throwError(() => err);
        }

        // Refresh-and-retry only makes sense when a KC session exists. On
        // anonymous public pages (estimate/invitation links) keycloak is
        // enabled but never initialized — a 401 there must fall through to
        // the plain handling below, which respects redirectToLogin (the
        // public estimate effect probes an authenticated endpoint and turns
        // the redirect off around that call).
        if (this.keycloak.enabled && this.keycloak.authenticated) {
          return this.handle401(request, next, err);
        }

        localStorage.removeItem('token');
        if (this.redirectToLogin) {
          this.keycloak.enabled
            ? this.keycloak.login(window.location.href)
            : this.router.navigate(['/auth/login']);
        }

        return throwError(() => err);
      }),
    );
  }

  private handle401(
    request: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    // The identity itself is already known to be rejected — retrying just
    // burns another request per call site. Let the error surface.
    if (this.identityRejected) {
      return throwError(() => err);
    }

    if (this.refreshInFlight === null) {
      this.refreshInFlight = this.keycloak
        .getToken()
        .catch(() => '')
        // Cleared on the next tick: long enough to collapse this burst of
        // 401s into one refresh, short enough that a genuine expiry later in
        // the session still gets its own.
        .finally(() => setTimeout(() => (this.refreshInFlight = null), 0));
    }

    return from(this.refreshInFlight).pipe(
      switchMap((newToken: string) => {
        // No token means the KC session is gone and getToken() has already
        // started a real login redirect. Nothing left to retry.
        if (!newToken) {
          return throwError(() => err);
        }

        return next
          .handle(request.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
          .pipe(
            catchError((retryErr: unknown) => {
              if (retryErr instanceof HttpErrorResponse && retryErr.status === 401) {
                this.onIdentityRejected(request.url);
              }
              return throwError(() => retryErr);
            }),
          );
      }),
    );
  }

  /**
   * A brand-new access token was still rejected: this is an authorization
   * problem with the signed-in identity, not an expired session. Say so once,
   * loudly, and offer the only exit that changes the outcome.
   */
  private onIdentityRejected(url: string): void {
    if (this.identityRejected) return;
    this.identityRejected = true;

    console.error(
      `[auth] Backend returned 401 for ${url} with a freshly refreshed Keycloak token — ` +
        'the session is valid but this identity is not accepted here. Not retrying.',
    );
    this.router.navigate(['/switch-account']);
  }
}
